import { BaseApiClient } from './base-client';
import { ActivityResponse, ActivityLog, PaginatedActivityResponse, ActivityFilter } from '../types/activity';

export class ActivityApiClient extends BaseApiClient {
  async getActivity(skip: number = 0, limit: number = 50): Promise<ActivityResponse> {
    return this.get<ActivityResponse>('/api/v1/activity/', {
      params: { skip, limit }
    });
  }

  async getFilteredActivity(filters: ActivityFilter = {}): Promise<PaginatedActivityResponse> {
    // Remove empty string values to avoid validation errors
    const cleanFilters = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
    
    return this.get<PaginatedActivityResponse>('/api/v1/activity/filtered', {
      params: cleanFilters
    });
  }

  async getServiceAuditLogs(serviceId: number, skip: number = 0, limit: number = 50): Promise<ActivityLog[]> {
    return this.get<ActivityLog[]>(`/api/v1/activity/audit/${serviceId}`, {
      params: { skip, limit }
    });
  }

  // Helper method to format activity action for display
  formatActivityAction(action: string): string {
    const actionMap: { [key: string]: string } = {
      'User logged in': 'Logged in',
      'Service created': 'Service created',
      'Service terminated': 'Service terminated',
      'Service quarantined': 'Service quarantined',
      'Service restored': 'Service restored',
      'VPN user created': 'VPN user created',
      'VPN user deleted': 'VPN user deleted',
      'Samba share created': 'Samba share created',
      'Samba share deleted': 'Samba share deleted',
      'NFS mount created': 'NFS mount created',
      'NFS mount deleted': 'NFS mount deleted',
      'User created': 'User created',
      'User deleted': 'User deleted',
      'Group created': 'Group created',
      'Group deleted': 'Group deleted',
      'Settings updated': 'Settings updated',
      'Billing info updated': 'Billing information updated',
      'Payment completed': 'Payment completed',
      'Order created': 'Order created'
    };
    return actionMap[action] || action;
  }

  // Helper method to get activity action color/type
  getActivityActionType(action: string): 'info' | 'success' | 'warning' | 'error' {
    const typeMap: { [key: string]: 'info' | 'success' | 'warning' | 'error' } = {
      'User logged in': 'info',
      'Service created': 'success',
      'Service terminated': 'error',
      'Service quarantined': 'warning',
      'Service restored': 'success',
      'VPN user created': 'success',
      'VPN user deleted': 'warning',
      'Samba share created': 'success',
      'Samba share deleted': 'warning',
      'NFS mount created': 'success',
      'NFS mount deleted': 'warning',
      'User created': 'success',
      'User deleted': 'warning',
      'Group created': 'success',
      'Group deleted': 'warning',
      'Settings updated': 'info',
      'Billing info updated': 'info',
      'Payment completed': 'success',
      'Order created': 'success'
    };
    return typeMap[action] || 'info';
  }

  // Helper method to format timestamp for display
  formatTimestamp(timestamp: string): string {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  }

  // Helper method to get relative time
  getRelativeTime(timestamp: string): string {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffMs = now.getTime() - activityTime.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) {
      return 'Just now';
    } else if (diffMinutes < 60) {
      return `${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    } else {
      return this.formatTimestamp(timestamp);
    }
  }

  // Helper method to group activities by date
  groupActivitiesByDate(activities: ActivityLog[]): { [date: string]: ActivityLog[] } {
    return activities.reduce((groups, activity) => {
      const date = new Date(activity.timestamp).toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(activity);
      return groups;
    }, {} as { [date: string]: ActivityLog[] });
  }
}